import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CustomSelect from '../components/CustomSelect';

const quickFilterOptions = [
  { id: 'all', label: 'All' },
  { id: 'locked', label: 'Locked' },
  { id: 'behavior', label: 'Behavior' },
  { id: 'relationships', label: 'Requests' }
];

const deletableStatuses = new Set(['completed', 'failed', 'cancelled']);

export default function SavedRunsView({
  savedRunsWorkspace,
  searchTerm,
  isLoading,
  onSelectRun,
  onRenameRun,
  onDeleteRun,
  onApplyRunAssignments,
  onRefreshRun,
  terminology
}) {
  const [activeQuickFilter, setActiveQuickFilter] = useState('all');
  const [selectedForApply, setSelectedForApply] = useState(new Set());
  const [isApplying, setIsApplying] = useState(false);
  const [applySuccessMessage, setApplySuccessMessage] = useState('');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('all');
  const [placementFilter, setPlacementFilter] = useState('all');
  const [relationshipFilter, setRelationshipFilter] = useState('all');

  const [isRenaming, setIsRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameError, setRenameError] = useState('');
  const [isSavingRename, setIsSavingRename] = useState(false);
  const [deletingRunId, setDeletingRunId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isScoreExplainerOpen, setIsScoreExplainerOpen] = useState(false);
  const [flashStudentId, setFlashStudentId] = useState(null);
  const flashTimeoutRef = useRef(null);
  const selectedRunId = savedRunsWorkspace?.selectedRun?.id ?? '';
  const { runId: routeRunId } = useParams();
  const navigate = useNavigate();
  const isDetailRoute = Boolean(routeRunId);

  const renameErrorTimerRef = useRef(null);
  const detailSectionRef = useRef(null);

  useEffect(() => {
    setActiveQuickFilter('all');
    setGradeFilter('all');
    setClassFilter('all');
    setPlacementFilter('all');
    setRelationshipFilter('all');
    setIsRenaming(false);
    setRenameDraft('');
    setRenameError('');
  }, [selectedRunId]);

  useEffect(() => {
    if (renameErrorTimerRef.current) {
      clearTimeout(renameErrorTimerRef.current);
    }
    if (renameError) {
      renameErrorTimerRef.current = setTimeout(() => {
        setRenameError('');
      }, 4000);
    }
    return () => clearTimeout(renameErrorTimerRef.current);
  }, [renameError]);

  useEffect(() => {
    if (isDetailRoute && selectedRunId && detailSectionRef.current) {
      detailSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isDetailRoute, selectedRunId]);

  const selectedRun = savedRunsWorkspace?.selectedRun;
  const aiNotesPending = selectedRun?.aiNotesStatus === 'pending';

  useEffect(() => {
    if (!aiNotesPending || !onRefreshRun || !selectedRunId) return;
    const intervalId = setInterval(() => {
      onRefreshRun(selectedRunId);
    }, 2000);
    return () => clearInterval(intervalId);
  }, [aiNotesPending, selectedRunId, onRefreshRun]);

  if (isLoading || !savedRunsWorkspace) {
    return (
      <section className="panel roster-loading">
        <div className="loading-card">
          <div className="spinner-ring" />
          <h1>Loading saved runs</h1>
          <p>Preparing the saved runs workspace.</p>
        </div>
      </section>
    );
  }

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredRuns = normalizedSearch
    ? savedRunsWorkspace.runs.filter((run) => run.name.toLowerCase().includes(normalizedSearch))
    : savedRunsWorkspace.runs;
  const detailRun = selectedRun && isDetailRoute ? selectedRun : null;
  const assignments = detailRun?.assignments ?? [];
  const gradeOptions = collectUniqueValues(assignments, 'gradeLevel');
  const classOptions = collectUniqueValues(assignments, 'assignedClassName');
  const quickFilters = quickFilterOptions.map((filter) => ({
    ...filter,
    count: assignments.filter((assignment) => matchesQuickFilter(assignment, filter.id)).length
  }));
  const filteredAssignments = assignments.filter((assignment) => {
    if (!matchesSearch(assignment, normalizedSearch)) {
      return false;
    }

    if (!matchesQuickFilter(assignment, activeQuickFilter)) {
      return false;
    }

    if (gradeFilter !== 'all' && assignment.gradeLevel !== gradeFilter) {
      return false;
    }

    if (classFilter !== 'all' && assignment.assignedClassName !== classFilter) {
      return false;
    }

    if (placementFilter !== 'all' && assignment.placementSource !== placementFilter) {
      return false;
    }

    if (!matchesRelationshipFilter(assignment, relationshipFilter)) {
      return false;
    }

    return true;
  });

  function handleScrollToStudent(studentId) {
    const row = document.getElementById(`saved-run-row-${studentId}`);
    if (!row) return;

    row.scrollIntoView({ behavior: 'smooth', block: 'center' });

    if (flashTimeoutRef.current) {
      clearTimeout(flashTimeoutRef.current);
    }
    setFlashStudentId(studentId);
    flashTimeoutRef.current = setTimeout(() => {
      setFlashStudentId(null);
      flashTimeoutRef.current = null;
    }, 1800);
  }

  function handleSortClick(column) {
    if (sortColumn === column) {
      setSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }

  const sortedAssignments = [...filteredAssignments].sort((a, b) => {
    let cmp = 0;
    if (sortColumn === 'name') {
      cmp = a.displayName.localeCompare(b.displayName);
    } else if (sortColumn === 'assigned') {
      cmp = a.assignedClassName.localeCompare(b.assignedClassName);
    }
    return sortDirection === 'asc' ? cmp : -cmp;
  });

  const areFiltersActive = [
    activeQuickFilter !== 'all',
    gradeFilter !== 'all',
    classFilter !== 'all',
    placementFilter !== 'all',
    relationshipFilter !== 'all'
  ].some(Boolean);
  const hasRuns = savedRunsWorkspace.runs.length > 0;

  function clearFilters() {
    setActiveQuickFilter('all');
    setGradeFilter('all');
    setClassFilter('all');
    setPlacementFilter('all');
    setRelationshipFilter('all');
  }

  function handleStartRename() {
    if (!selectedRun) return;
    setRenameDraft(selectedRun.name);
    setRenameError('');
    setIsRenaming(true);
  }

  function handleCancelRename() {
    setIsRenaming(false);
    setRenameDraft('');
    setRenameError('');
  }

  async function handleSaveRename() {
    const trimmed = renameDraft.trim();
    if (!trimmed) {
      setRenameError('Name cannot be empty.');
      return;
    }
    if (trimmed === selectedRun?.name) {
      handleCancelRename();
      return;
    }
    setIsSavingRename(true);
    setRenameError('');
    try {
      await onRenameRun(selectedRun.id, trimmed);
      setIsRenaming(false);
      setRenameDraft('');
    } catch (error) {
      setRenameError(error?.message || 'Failed to rename.');
    } finally {
      setIsSavingRename(false);
    }
  }

  function handleRenameKeyDown(event) {
    if (event.key === 'Enter') {
      handleSaveRename();
    } else if (event.key === 'Escape') {
      handleCancelRename();
    }
  }

  function handleDeleteRunClick(run, event) {
    if (event) {
      event.stopPropagation();
    }
    if (!deletableStatuses.has(run.statusKey)) {
      return;
    }
    setConfirmDelete(run);
  }

  async function handleConfirmDelete() {
    if (!confirmDelete || !onDeleteRun) {
      return;
    }
    setDeletingRunId(confirmDelete.id);
    try {
      await onDeleteRun(confirmDelete.id);
      navigate('/saved-runs');
    } finally {
      setDeletingRunId(null);
      setConfirmDelete(null);
    }
  }

  function handleCancelDelete() {
    setConfirmDelete(null);
  }

  function handleExportAssignmentsClick() {
    if (!sortedAssignments.length) {
      return;
    }
    const header = ['Student', 'Grade', 'Assigned Class'];
    const rows = sortedAssignments.map((assignment) => [
      assignment.displayName,
      assignment.gradeLevel,
      assignment.assignedClassName
    ]);
    const csv = [header, ...rows]
      .map((row) =>
        row
          .map((cell) => {
            const safe = String(cell ?? '').replace(/"/g, '""');
            return `"${safe}"`;
          })
          .join(',')
      )
      .join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const safeName = (selectedRun.name || 'assignments').replace(/[^a-zA-Z0-9_-]/g, '_');
    link.setAttribute('download', `${safeName}-assignments.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function handleToggleApplyRow(studentId) {
    setSelectedForApply((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  }

  function handleSelectAllForApply() {
    setSelectedForApply(new Set(sortedAssignments.map((a) => a.studentId)));
  }

  function handleDeselectAllForApply() {
    setSelectedForApply(new Set());
  }

  async function handleApplySelected() {
    if (!selectedRun || selectedForApply.size === 0 || !onApplyRunAssignments) return;
    const count = selectedForApply.size;
    setIsApplying(true);
    setApplySuccessMessage('');
    try {
      await onApplyRunAssignments(selectedRun.id, Array.from(selectedForApply));
      setSelectedForApply(new Set());
      setApplySuccessMessage(`${count} ${count === 1 ? 'assignment' : 'assignments'} applied`);
      setTimeout(() => setApplySuccessMessage(''), 4000);
    } finally {
      setIsApplying(false);
    }
  }

  function handleBackToRunsClick() {
    navigate('/saved-runs');
  }

  return (
    <div className="saved-runs-screen">
      {!(isDetailRoute && selectedRun) ? (<>
      <section className="hero-card roster-hero">
        <div className="hero-copy">
          <div className="eyebrow-row">
            <span className="tone-pill accent">Saved runs</span>
            <span className="eyebrow-copy">
              {savedRunsWorkspace.summary.completedRuns} completed run{savedRunsWorkspace.summary.completedRuns === 1 ? '' : 's'}
            </span>
          </div>
          <h1>{savedRunsWorkspace.project.name}</h1>
          <p>
            Review saved run outputs against the exact roster state that was captured when each
            run finished.
          </p>
        </div>

        <div className="hero-actions">
          <div className="roster-stat-block">
            <span className="metric-label">All runs</span>
            <strong>{savedRunsWorkspace.summary.totalRuns}</strong>
          </div>
          <div className="roster-stat-block">
            <span className="metric-label">Completed</span>
            <strong>{savedRunsWorkspace.summary.completedRuns}</strong>
          </div>
          <div className="roster-stat-block">
            <span className="metric-label">Failed</span>
            <strong>{savedRunsWorkspace.summary.failedRuns}</strong>
          </div>
        </div>
      </section>

      <section className="panel saved-runs-library-panel">
        <div className="panel-head">
          <div>
            <span className="panel-kicker">Run library</span>
            <h2>Saved runs</h2>
          </div>
          <span className="toolbar-meta">{filteredRuns.length} {filteredRuns.length === 1 ? 'run' : 'runs'}</span>
        </div>

        <div className="table-wrap">
          <table className="data-grid runs-table">
            <thead>
              <tr>
                <th>Run</th>
                <th>Status</th>
                <th>Date</th>
                <th>
                  Score
                  <button
                    type="button"
                    className="score-help-icon"
                    title="How is the score calculated?"
                    onClick={(e) => { e.stopPropagation(); setIsScoreExplainerOpen(true); }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </button>
                </th>
                <th>Matched requests</th>
                <th>Keep-away resolved</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredRuns.length > 0 ? (
                filteredRuns.map((run) => {
                  const canDelete = deletableStatuses.has(run.statusKey);
                  const isDeleting = deletingRunId === run.id;
                  const isSelected = selectedRunId === run.id;
                  return (
                    <tr
                      key={run.id}
                      className={isSelected ? 'runs-table-row is-selected-row' : 'runs-table-row'}
                      onClick={() => onSelectRun(run.id)}
                    >
                      <td>
                        <div className="student-cell">
                          <strong>{run.name}</strong>
                          <span>
                            {run.gradeLevel ? `Grade ${run.gradeLevel} • ` : ''}{run.createdAtLabel}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`tone-pill ${run.statusTone}`}>
                          {renderStatusIcon(run.statusKey)}
                          <span className="runs-status-label">{run.status}</span>
                        </span>
                      </td>
                      <td>{run.createdAtLabel}</td>
                      <td>{run.score != null ? run.score.toFixed(1) : '—'}</td>
                      <td>{run.friendMatches}</td>
                      <td>{run.keepAwayResolved}</td>
                      <td>
                        {canDelete ? (
                          <button
                            type="button"
                            className="btn-small btn-danger"
                            disabled={isDeleting}
                            onClick={(e) => handleDeleteRunClick(run, e)}
                          >
                            {isDeleting ? 'Deleting...' : 'Delete'}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="empty-grid-cell">
                    {hasRuns
                      ? 'No runs match the current search or filters.'
                      : 'No runs have been generated yet.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      </>) : null}

      {isDetailRoute && selectedRun ? (
        <section className="panel saved-runs-main-panel" ref={detailSectionRef}>
          <div className="panel-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <span className="panel-kicker">Run detail · {new Date(selectedRun.createdAt).toLocaleString()}</span>
              {isRenaming ? (
                <div className="inline-rename-row">
                  <input
                    type="text"
                    className="inline-rename-input"
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onKeyDown={handleRenameKeyDown}
                    disabled={isSavingRename}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="btn-small btn-primary"
                    onClick={handleSaveRename}
                    disabled={isSavingRename}
                  >
                    {isSavingRename ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="btn-small btn-ghost"
                    onClick={handleCancelRename}
                    disabled={isSavingRename}
                  >
                    Cancel
                  </button>
                  {renameError && <span key={renameError} className="inline-rename-error fade-out">{renameError}</span>}
                </div>
              ) : (
                <>
                  <div className="inline-rename-row">
                    <button
                      type="button"
                      className="utility-button compact-button"
                      onClick={handleBackToRunsClick}
                    >
                      ← Back to runs
                    </button>
                    <h2>{selectedRun.name}</h2>
                    <button
                      type="button"
                      className="icon-btn-inline"
                      title="Rename this run"
                      onClick={handleStartRename}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                      </svg>
                    </button>
                  </div>
                  <div className="saved-run-info-pills" style={{ marginTop: '8px' }}>
                    <span className={`tone-pill ${selectedRun.statusTone}`}>{selectedRun.status}</span>
                    {selectedRun.score != null ? <span className="soft-chip">Score {selectedRun.score.toFixed(1)}</span> : null}
                    {selectedRun.score != null ? (
                      <button type="button" className="score-explain-link" onClick={() => setIsScoreExplainerOpen(true)}>
                        What does this mean?
                      </button>
                    ) : null}
                  </div>
                  {aiNotesPending || selectedRun.notes ? (
                    <div className="ai-notes-section">
                      <h4 className="ai-notes-heading">AI Summary</h4>
                      {aiNotesPending ? (
                        <div className="ai-notes-loading">
                          <img src="/rotate.gif" alt="" className="ai-notes-spinner" />
                          <span className="ai-notes-loading-text">Loading AI summary<span className="animated-dots" /></span>
                        </div>
                      ) : (
                        <p className="saved-run-notes">{selectedRun.notes}</p>
                      )}
                    </div>
                  ) : null}
                </>
              )}
            </div>

            {deletableStatuses.has(selectedRun.statusKey) ? (
              <button
                type="button"
                className="btn-small btn-danger"
                style={{ flexShrink: 0, fontSize: 14, padding: '8px 20px' }}
                disabled={deletingRunId === selectedRun.id}
                onClick={() => handleDeleteRunClick(selectedRun)}
              >
                {deletingRunId === selectedRun.id ? 'Deleting...' : 'Delete'}
              </button>
            ) : null}
          </div>

          <RunClassSummary assignments={assignments} terminology={terminology} />


          <div className="saved-run-tab-strip">
            {quickFilters.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={`saved-run-tab ${activeQuickFilter === filter.id ? 'is-active' : ''}`}
                onClick={() => setActiveQuickFilter(filter.id)}
              >
                <span>{filter.label}</span>
                <strong>{filter.count}</strong>
              </button>
            ))}
          </div>

          <div className="saved-run-filter-row">
            <CustomSelect
              label="Grade"
              value={gradeFilter}
              options={[
                { value: 'all', label: 'All' },
                ...gradeOptions.map((grade) => ({ value: grade, label: grade }))
              ]}
              onChange={setGradeFilter}
            />

            <CustomSelect
              label={terminology.class}
              value={classFilter}
              options={[
                { value: 'all', label: 'All' },
                ...classOptions.map((cls) => ({ value: cls, label: cls }))
              ]}
              onChange={setClassFilter}
            />

            <CustomSelect
              label="Placement"
              value={placementFilter}
              options={[
                { value: 'all', label: 'All' },
                { value: 'optimized', label: 'Optimized' },
                { value: 'locked', label: 'Locked' }
              ]}
              onChange={setPlacementFilter}
            />

            <CustomSelect
              label="Relationships"
              value={relationshipFilter}
              options={[
                { value: 'all', label: 'All' },
                { value: 'any', label: 'Any' },
                { value: 'friend', label: 'Friend requests' },
                { value: 'keep-away', label: 'Keep away' }
              ]}
              onChange={setRelationshipFilter}
            />

            {areFiltersActive ? (
              <button type="button" className="saved-run-clear-button" onClick={clearFilters}>
                Clear filters
              </button>
            ) : null}
          </div>

          <div className="saved-run-results-bar">
            <span>{filteredAssignments.length} {terminology.students.toLowerCase()} shown</span>
            {normalizedSearch ? (
              <span>Filtered by &ldquo;{searchTerm.trim()}&rdquo;</span>
            ) : null}
            <button
              type="button"
              className="utility-button compact-button"
              onClick={handleExportAssignmentsClick}
              disabled={sortedAssignments.length === 0}
            >
              Export CSV
            </button>
          </div>

          {onApplyRunAssignments ? (
            <div className="saved-run-apply-bar">
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button type="button" className="utility-button compact-button" onClick={handleSelectAllForApply}>
                  Select all
                </button>
                <button type="button" className="utility-button compact-button" onClick={handleDeselectAllForApply}>
                  Deselect all
                </button>
                <span style={{ fontSize: '0.8rem', color: 'var(--muted)', marginLeft: '0.25rem' }}>
                  {selectedForApply.size} of {sortedAssignments.length} selected
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                {applySuccessMessage ? (
                  <span style={{ fontSize: '0.8rem', color: 'var(--good, #22c55e)', fontWeight: 600 }}>
                    ✓ {applySuccessMessage}
                  </span>
                ) : null}
                <button
                  type="button"
                  className="primary-button compact-button"
                  disabled={selectedForApply.size === 0 || isApplying}
                  onClick={handleApplySelected}
                >
                  {isApplying ? <span className="button-spinner" aria-hidden="true" /> : null}
                  Apply selected to planner
                </button>
              </div>
            </div>
          ) : null}

          <div className="table-wrap">
            <table className="data-grid saved-run-grid-table">
              <thead>
                <tr>
                  {onApplyRunAssignments ? (
                    <th style={{ width: '2.5rem' }}>
                      <input
                        type="checkbox"
                        checked={selectedForApply.size > 0 && selectedForApply.size === sortedAssignments.length}
                        onChange={() => selectedForApply.size === sortedAssignments.length ? handleDeselectAllForApply() : handleSelectAllForApply()}
                      />
                    </th>
                  ) : null}
                  <th className="sortable-th" onClick={() => handleSortClick('name')}>
                    {terminology.student} {sortColumn === 'name' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                  </th>
                  <th className="sortable-th" onClick={() => handleSortClick('assigned')}>
                    Assigned {sortColumn === 'assigned' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                  </th>
                  <th>Lock</th>
                  <th>Friend requests</th>
                  <th>Keep-away</th>
                  <th>Behavior</th>
                  {(selectedRun.factorDefinitions ?? []).map((def) => (
                    <th key={def.slug}>{def.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedAssignments.length > 0 ? sortedAssignments.map((assignment) => (
                  <tr
                    key={`${selectedRun.id}-${assignment.studentId}`}
                    id={`saved-run-row-${assignment.studentId}`}
                    className={flashStudentId === assignment.studentId ? 'flash-row' : ''}
                  >
                    {onApplyRunAssignments ? (
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedForApply.has(assignment.studentId)}
                          onChange={() => handleToggleApplyRow(assignment.studentId)}
                        />
                      </td>
                    ) : null}
                    <td>
                      <div className="student-cell">
                        <strong>{assignment.displayName}</strong>
                        <span>
                          {assignment.externalStudentId} • {assignment.gradeLevel}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div
                        className="list-cell assigned-cell"
                        onClick={() => setClassFilter(
                          classFilter === assignment.assignedClassName ? 'all' : assignment.assignedClassName
                        )}
                      >
                        <span className="assigned-name-row">
                          <strong>{assignment.assignedClassName}</strong>
                          <span className="assigned-only-hint">only</span>
                        </span>
                      </div>
                    </td>
                    <td>
                      {assignment.placementSource === 'locked' ? (
                        <span className="planner-lock-chip is-locked">
                          Locked to {terminology.class}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <RelationshipChips
                        items={assignment.friendRelationships}
                        fallbackNames={assignment.friendNames}
                        type="friend"
                        onChipClick={handleScrollToStudent}
                      />
                    </td>
                    <td>
                      <RelationshipChips
                        items={assignment.keepAwayRelationships}
                        fallbackNames={assignment.keepAwayNames}
                        type="keep-away"
                        onChipClick={handleScrollToStudent}
                      />
                    </td>
                    <td>
                      {assignment.behaviorFlag ? (
                        <span className="behavior-pill monitor">Monitor</span>
                      ) : (
                        <span className="behavior-pill steady">Steady</span>
                      )}
                    </td>
                    {(selectedRun.factorDefinitions ?? []).map((def) => (
                      <td key={def.slug}>
                        {formatFactorValue(def, assignment.customFactors?.[def.slug])}
                      </td>
                    ))}
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={6 + (selectedRun.factorDefinitions ?? []).length} className="empty-grid-cell">
                      {assignments.length > 0
                        ? `No ${terminology.students.toLowerCase()} match the current saved-run filters.`
                        : `No frozen ${terminology.student.toLowerCase()} assignments are available for this run yet.`}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {/* empty-state card removed — the table already shows "No runs have been generated yet." */}

      {confirmDelete ? (
        <div className="workspace-dialog-backdrop" onClick={handleCancelDelete}>
          <div
            className="workspace-dialog confirm-delete-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-delete-title"
            aria-describedby="confirm-delete-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="confirm-delete-title">Delete run</h2>
            <p id="confirm-delete-desc" className="confirm-delete-message">
              Are you sure you want to delete <strong>{confirmDelete.name}</strong>? This action cannot be undone.
            </p>
            <div className="confirm-delete-actions">
              <button
                type="button"
                className="btn-small btn-ghost"
                onClick={handleCancelDelete}
                disabled={deletingRunId === confirmDelete.id}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-small btn-danger"
                onClick={handleConfirmDelete}
                disabled={deletingRunId === confirmDelete.id}
              >
                {deletingRunId === confirmDelete.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isScoreExplainerOpen ? (
        <div className="workspace-dialog-backdrop" onClick={() => setIsScoreExplainerOpen(false)}>
          <div
            className="workspace-dialog panel"
            style={{ width: 'min(520px, calc(100vw - 48px))' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="panel-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <span className="panel-kicker">ABOUT</span>
                <h2>How the Score Works</h2>
              </div>
              <button type="button" className="icon-button classes-dialog-close" onClick={() => setIsScoreExplainerOpen(false)}>&times;</button>
            </div>
            <div className="score-explainer-body" style={{ padding: '0 20px 20px' }}>
              <p>
                The score is a weighted quality rating from 0 to 100 that measures how well the solver satisfied your placement constraints. A higher score means a better placement.
              </p>
              <h3>Components</h3>
              <ul>
                <li><strong>Placement rate (25%)</strong> — What fraction of {terminology.student.toLowerCase()}s were successfully assigned to a {terminology.class.toLowerCase()}.</li>
                <li><strong>Friend match rate (25%)</strong> — What fraction of friend requests were satisfied (both {terminology.student.toLowerCase()}s placed together).</li>
                <li><strong>Keep-away rate (20%)</strong> — What fraction of keep-away pairs were successfully separated into different {terminology.class.toLowerCase()}es.</li>
                <li><strong>Capacity compliance (10%)</strong> — What fraction of {terminology.class.toLowerCase()}es are at or under their capacity limit.</li>
                <li><strong>Balance compliance (20%)</strong> — How evenly your enabled factors (gender, behavior, academic, etc.) are distributed across {terminology.class.toLowerCase()}es.</li>
              </ul>
              <h3>Interpreting the Score</h3>
              <ul>
                <li><strong>90+</strong> — Great result. Well-balanced with most constraints met.</li>
                <li><strong>70–89</strong> — Good result. Most constraints met, but some adjustments may help.</li>
                <li><strong>Below 70</strong> — Some constraints could not be satisfied. Consider adjusting {terminology.class.toLowerCase()} capacities or reviewing conflicting requests.</li>
              </ul>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function RunClassSummary({ assignments, terminology }) {
  if (!assignments.length) {
    return null;
  }

  const summaries = buildClassSummaries(assignments);
  if (!summaries.length) {
    return null;
  }

  const studentLabel = terminology.students.toLowerCase();

  return (
    <div className="class-donut-strip">
      {summaries.map((summary) => {
        const maleCount = Math.round((summary.malePercent / 100) * summary.totalStudents);
        const femaleCount = Math.round((summary.femalePercent / 100) * summary.totalStudents);
        const capacity = summary.capacity ?? summary.totalStudents;
        const isOver = summary.totalStudents > capacity;
        const total = Math.max(capacity, summary.totalStudents) || 1;
        const girlsFrac = femaleCount / total;
        const boysFrac = maleCount / total;
        const otherFrac = Math.max(0, summary.totalStudents - maleCount - femaleCount) / total;
        const r = 36;
        const c = 2 * Math.PI * r;
        const girlsLen = girlsFrac * c;
        const boysLen = boysFrac * c;
        const otherLen = otherFrac * c;
        const girlsOffset = 0;
        const boysOffset = -girlsLen;
        const otherOffset = -(girlsLen + boysLen);
        const friendPct = Math.round(summary.matchedPreferenceRate ?? 0);

        return (
          <article key={summary.id} className={`class-donut-card ${isOver ? 'is-over' : ''}`}>
            <span className="class-donut-name">{summary.label}</span>
            <div className="class-donut-ring-wrap">
              <svg viewBox="0 0 80 80" className="class-donut-svg">
                <circle cx="40" cy="40" r={r} fill="none" stroke="#eef1f5" strokeWidth="7" />
                {isOver ? (
                  <circle cx="40" cy="40" r={r} fill="none" stroke="var(--danger, #e53e3e)" strokeWidth="7" />
                ) : (
                  <>
                    {femaleCount > 0 ? (
                      <circle cx="40" cy="40" r={r} fill="none" stroke="#f0a0be" strokeWidth="7"
                        strokeDasharray={`${girlsLen} ${c - girlsLen}`}
                        strokeDashoffset={girlsOffset}
                        transform="rotate(-90 40 40)"
                      />
                    ) : null}
                    {maleCount > 0 ? (
                      <circle cx="40" cy="40" r={r} fill="none" stroke="#8eaff0" strokeWidth="7"
                        strokeDasharray={`${boysLen} ${c - boysLen}`}
                        strokeDashoffset={boysOffset}
                        transform="rotate(-90 40 40)"
                      />
                    ) : null}
                    {otherFrac > 0 ? (
                      <circle cx="40" cy="40" r={r} fill="none" stroke="#b0b8c4" strokeWidth="7"
                        strokeDasharray={`${otherLen} ${c - otherLen}`}
                        strokeDashoffset={otherOffset}
                        transform="rotate(-90 40 40)"
                      />
                    ) : null}
                  </>
                )}
              </svg>
              <div className="class-donut-center">
                <span className={`class-donut-count ${isOver ? 'is-over' : ''}`}>{summary.totalStudents}</span>
                <span className="class-donut-cap">{studentLabel}</span>
              </div>
            </div>
            <div className="class-donut-legend">
              <span className="class-donut-legend-item"><span className="class-donut-dot" style={{ background: '#f0a0be' }} />{femaleCount} Girls</span>
              <span className="class-donut-legend-item"><span className="class-donut-dot" style={{ background: '#8eaff0' }} />{maleCount} Boys</span>
            </div>
            <div className="class-donut-legend">
              {friendPct > 0 ? (
                <span className="class-donut-legend-item" style={{ color: 'var(--good, #16a34a)' }}>
                  {friendPct}% with friends
                </span>
              ) : null}
              {summary.behaviorIssues > 0 ? (
                <span className="class-donut-behavior">{summary.behaviorIssues} behavior</span>
              ) : null}
            </div>
          </article>
        );
      })}
    </div>
  );
}

function RelationshipChips({ items = [], fallbackNames = [], type, onChipClick }) {
  const hasStructured = Array.isArray(items) && items.length > 0;
  const chips = hasStructured
    ? items
    : (fallbackNames ?? []).map((name) => ({
        relatedStudentId: name,
        relatedStudentName: name
      }));

  if (!chips.length) {
    return null;
  }

  return (
    <div className="relationship-chip-row">
      {chips.map((chip) => {
        const key = chip.relatedStudentId ?? chip.relatedStudentName;
        const isGood = type === 'friend' ? chip.matched === true : chip.honored === true;
        const isBad = type === 'friend' ? chip.matched === false : chip.honored === false;
        const toneClass = type === 'keep-away'
          ? (isBad ? 'relationship-chip-bad' : 'relationship-chip-keep-away')
          : (isGood ? 'relationship-chip-good' : isBad ? 'relationship-chip-bad' : '');
        const typeClass = '';
        const symbol = isGood ? '✓' : isBad ? '✕' : '•';

        return (
          <span
            key={key}
            className={`relationship-chip ${toneClass} ${typeClass} ${onChipClick ? 'relationship-chip-clickable' : ''}`}
            onClick={onChipClick ? () => onChipClick(chip.relatedStudentId) : undefined}
          >
            <span className="relationship-chip-symbol">{symbol}</span>
            <span>{chip.relatedStudentName}</span>
          </span>
        );
      })}
    </div>
  );
}

function buildClassSummaries(assignments) {
  const groups = new Map();
  let totalBehaviorIssues = 0;

  for (const assignment of assignments) {
    const key = `${assignment.gradeLevel ?? ''}::${assignment.assignedClassName ?? ''}`;
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(assignment);
    if (assignment.behaviorFlag) {
      totalBehaviorIssues += 1;
    }
  }

  const summaries = [];

  for (const [key, group] of groups.entries()) {
    const [gradeLevel, className] = key.split('::');
    const total = group.length;
    if (total === 0) continue;

    let male = 0;
    let female = 0;
    let behaviorIssues = 0;
    let scoreSum = 0;
    let scoreCount = 0;
    let studentsWithRelationships = 0;
    let overrideViolations = 0;

    for (const a of group) {
      const gender = String(a.gender || '').toLowerCase();
      if (gender.startsWith('m')) male += 1;
      if (gender.startsWith('f')) female += 1;
      if (a.behaviorFlag) behaviorIssues += 1;
      if (Number.isFinite(a.academicScore)) {
        scoreSum += a.academicScore;
        scoreCount += 1;
      }
      if ((a.friendCount ?? 0) > 0 || (a.keepAwayCount ?? 0) > 0) {
        studentsWithRelationships += 1;
      }
      if (a.placementSource === 'locked' || a.fixedClassName !== 'Open') {
        overrideViolations += 1;
      }
    }

    const malePercent = (male / total) * 100;
    const femalePercent = (female / total) * 100;
    const behaviorPercentOfClass = (behaviorIssues / total) * 100;
    const behaviorPercentOfAll = totalBehaviorIssues
      ? (behaviorIssues / totalBehaviorIssues) * 100
      : 0;
    const avgScore = scoreCount > 0 ? scoreSum / scoreCount : null;
    const matchedPreferenceRate = total
      ? (studentsWithRelationships / total) * 100
      : 0;

    summaries.push({
      id: key,
      label: className,
      totalStudents: total,
      malePercent,
      femalePercent,
      behaviorIssues,
      behaviorPercentOfClass,
      behaviorPercentOfAll,
      avgScore,
      matchedPreferenceRate,
      overrideViolations
    });
  }

  summaries.sort((left, right) => left.label.localeCompare(right.label, undefined, { numeric: true }));
  return summaries;
}

function renderStatusIcon(statusKey) {
  switch (statusKey) {
    case 'completed':
      return (
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          aria-hidden="true"
          style={{ marginRight: 4 }}
        >
          <path
            d="M7.999 1.333A6.667 6.667 0 1 0 14.666 8 6.675 6.675 0 0 0 7.999 1.333Zm0 12A5.333 5.333 0 1 1 13.333 8 5.339 5.339 0 0 1 7.999 13.333Zm2.863-7.53L7.4 9.8a.667.667 0 0 1-.943.038L5.14 8.62a.667.667 0 1 1 .92-.966l.86.82 3.02-2.86a.667.667 0 1 1 .922.949Z"
            fill="currentColor"
          />
        </svg>
      );
    case 'failed':
      return (
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          aria-hidden="true"
          style={{ marginRight: 4 }}
        >
          <path
            d="M8 1.333A6.667 6.667 0 1 0 14.667 8 6.675 6.675 0 0 0 8 1.333Zm0 12A5.333 5.333 0 1 1 13.333 8 5.339 5.339 0 0 1 8 13.333Zm1.886-7.552a.667.667 0 0 1 .943.943L8.943 8l1.886 1.276a.667.667 0 0 1-.743 1.105L8 9.057l-1.276 1.324a.667.667 0 1 1-.943-.943L7.057 8 5.781 6.724a.667.667 0 1 1 .943-.943L8 7.057Z"
            fill="currentColor"
          />
        </svg>
      );
    case 'running':
    case 'queued':
      return (
        <span className="runs-status-dot" aria-hidden="true" />
      );
    default:
      return null;
  }
}

function collectUniqueValues(assignments, key) {
  return [...new Set(assignments.map((assignment) => assignment[key]).filter(Boolean))].sort((left, right) =>
    String(left).localeCompare(String(right), undefined, { numeric: true })
  );
}

function matchesSearch(assignment, normalizedSearch) {
  if (!normalizedSearch) {
    return true;
  }

  return (
    assignment.displayName.toLowerCase().includes(normalizedSearch) ||
    assignment.externalStudentId.toLowerCase().includes(normalizedSearch) ||
    assignment.assignedClassName.toLowerCase().includes(normalizedSearch) ||
    assignment.gradeLevel.toLowerCase().includes(normalizedSearch)
  );
}

function matchesQuickFilter(assignment, quickFilter) {
  switch (quickFilter) {
    case 'locked':
      return assignment.placementSource === 'locked' || (assignment.fixedClassName && assignment.fixedClassName !== 'Open');
    case 'behavior':
      return assignment.behaviorFlag === true;
    case 'signals':
      return assignment.signals.length > 0;
    case 'relationships':
      return assignment.friendCount > 0 || assignment.keepAwayCount > 0;
    default:
      return true;
  }
}

function matchesRelationshipFilter(assignment, relationshipFilter) {
  switch (relationshipFilter) {
    case 'any':
      return assignment.friendCount > 0 || assignment.keepAwayCount > 0;
    case 'friend':
      return assignment.friendCount > 0;
    case 'keep-away':
      return assignment.keepAwayCount > 0;
    default:
      return true;
  }
}

function formatFactorValue(definition, value) {
  if (value === null || value === undefined || value === '') {
    return <span className="soft-chip">—</span>;
  }
  if (definition.dataType === 'boolean') {
    return value === true ? 'Yes' : 'No';
  }
  return String(value);
}

